import {
    BrowserCacheLocation,
    Configuration,
    LogLevel,
} from "@azure/msal-browser";

/**
 * Alignement Azure Portal ↔ backend Famla (`ENTRA_API_AUDIENCE` dans famla-api `.env`) :
 *
 * - Plateforme SPA : ajouter les URI de redirection (ex. `http://localhost:3000`, prod).
 * - « Expose an API » : Application ID URI (souvent `api://<client-id>`) et scopes délégués ;
 *   le claim `aud` du jeton d’accès doit correspondre à `ENTRA_API_AUDIENCE`.
 * - `REACT_APP_AZURE_SCOPES` : scopes séparés par des espaces (ex. `api://<client-id>/access_as_user openid profile email`).
 * - Ne pas appeler l’API Famla avec un jeton réservé à Microsoft Graph (`aud` Graph) : la validation échouera.
 */
const clientId = process.env.REACT_APP_AZURE_CLIENT_ID ?? "";
const tenant = process.env.REACT_APP_AZURE_TENANT ?? "organizations";

const scopesFromEnv =
    process.env.REACT_APP_AZURE_SCOPES?.split(/\s+/).filter(Boolean) ?? [];

const redirectOrigin =
    process.env.REACT_APP_AZURE_REDIRECT_ORIGIN ||
    process.env.REACT_APP_AZURE_REDIRECT_URI?.replace(/\/auth\/(login|signup)\/?$/, "") ||
    (typeof window !== "undefined" ? window.location.origin : "");

export const getMicrosoftRedirectUri = (path: "/auth/login" | "/auth/signup" = "/auth/login"): string => {
    const origin = redirectOrigin.replace(/\/+$/, "");
    return `${origin}${path}`;
};

/** Outlook dialog fallback — same Entra app (ffbf34bc) as web login. */
export const OFFICE_AUTH_DIALOG_PATH = "/embedded/office-auth";

export function getOfficeAuthRedirectUri(): string {
    const origin = redirectOrigin.replace(/\/+$/, "");
    return `${origin}${OFFICE_AUTH_DIALOG_PATH}`;
};

export const msalConfig: Configuration = {
    auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenant}`,
        redirectUri: getMicrosoftRedirectUri("/auth/login"),
    },
    cache: {
        // Persist across Outlook taskpane close/reopen (SessionStorage cleared with iframe)
        cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
        loggerOptions: {
            loggerCallback(_level, message, containsPii) {
                if (containsPii) return;
                if (process.env.REACT_APP_DEBUG === "true") {
                    // eslint-disable-next-line no-console
                    console.warn("[MSAL]", message);
                }
            },
            logLevel: LogLevel.Warning,
        },
    },
};

/** 
 * Scopes pour login et acquisition de jeton ; préférer des scopes API alignés sur « Expose an API ».
 * Le consentement Microsoft Graph Organization est géré côté backend via le flow OBO.
 * `prompt: "login"` force toujours la saisie du mot de passe, même si session Microsoft active.
 */
const baseScopes =
    scopesFromEnv.length > 0
        ? scopesFromEnv
        : ["openid", "profile", "offline_access"];

/** Ensure refresh tokens are available for ≥24h session continuity in Outlook. */
const defaultScopes = baseScopes.includes("offline_access")
    ? baseScopes
    : [...baseScopes, "offline_access"];

/** Login classique — force la ressaisie du mot de passe (bouton sur /auth/login). */
export const loginRequest = {
    scopes: defaultScopes,
    prompt: "login",
};

/** Acquisition silencieuse / session déjà MSAL — pas de challenge interactif. */
export const silentAuthRequest = {
    scopes: defaultScopes,
};

/** Office auth dialog (displayDialogAsync fallback) — mêmes scopes que le web. */
export const officeDialogLoginRequest = {
    scopes: defaultScopes,
};

/**
 * Flux Marketplace : pas de `prompt: login` pour éviter les challenges en boucle
 * quand une session Microsoft existe déjà.
 */
export const marketplaceLoginRequest = {
    scopes: defaultScopes,
    prompt: "select_account" as const,
};
